import React, { useState, useEffect } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { ToastContainer } from "react-toastify";
import {
  getAllCards,
  changeDateFilterFormat,
  getAnalytic,
  setDateForFilter,
} from "../../constants";
import { addDays } from "date-fns";
import ModalClickDateRange from "../modals/ModalClickDateRange";
import { calendar } from "../../assets";

// Register necessary components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const AnalyticDashboard = () => {
  const user = JSON.parse(localStorage.getItem("user_info"));
  const token = user.access_token;
  const today = new Date();
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [openDateRange, setOpenDateRange] = useState(false);
  const [summary, setSummary] = useState({
    total_new_users: null,
    total_social_card_clicks: null,
    total_users: null,
    total_views: null,
  });

  const filterEventCount = [
    {
      label: "Daily",
      value: "daily",
    },
    {
      label: "Weekly",
      value: "weekly",
    },
    {
      label: "Monthly",
      value: "monthly",
    },
  ];

  const filterViewsGeo = [
    {
      label: "City",
      value: "city",
    },
    {
      label: "Province",
      value: "province",
    },
    {
      label: "Country",
      value: "country",
    },
  ];

  const [eventCountFilter, setEventCountFilter] = useState(
    filterEventCount[0].value,
  );
  const [viewsGeoFilter, setViewsGeoFilter] = useState(filterViewsGeo[0].value);
  const [chartDataEventDay, setChartDataEventDay] = useState(null);
  const [chartDataEventWeek, setChartDataEventWeek] = useState(null);
  const [chartDataEventMonth, setChartDataEventMonth] = useState(null);
  const [chartDataEventOptions, setChartDataEventOptions] = useState({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        align: "start",
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "line",
        },
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true, // Set to false to show all labels
          maxRotation: 0, // Set to 0 to prevent rotation (if applicable)
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          autoSkip: true, // Set to false to show all labels
          maxRotation: 0, // Set to 0 to prevent rotation (if applicable)
        },
      },
    },
  });

  const [chartDataViewsGeoCity, setChartDataViewsGeoCity] = useState(null);
  const [chartDataViewsGeoProvince, setChartDataViewsGeoProvince] =
    useState(null);
  const [chartDataViewsGeoCountry, setChartDataViewsGeoCountry] =
    useState(null);
  const [chartDataClickSocial, setChartDataClickSocial] = useState(null);
  const [chartDataChannelMethod, setChartDataChannelMethod] = useState([]);
  const [chartDataViewsGeoOptions, setChartDataViewsGeoOptions] = useState({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          autoSkip: true, // Set to false to show all labels
          maxRotation: 0, // Set to 0 to prevent rotation (if applicable)
        },
      },
    },
  });

  const [dateRange, setDateRange] = useState([
    {
      startDate: addDays(today, -7),
      endDate: addDays(today, -1),
      key: "selection",
    },
  ]);
  const [filterDate, setFilterDate] = useState(null);

  const handleClickDateRange = (e) => {
    e.preventDefault();
    setOpenDateRange(!openDateRange);
  };

  useEffect(() => {
    getAllCards(token, setCards);
    getAnalytic(
      token,
      null,
      setDateForFilter(dateRange[0].startDate),
      setDateForFilter(dateRange[0].endDate),
      filterDate,
      setSummary,
      setChartDataEventDay,
      setChartDataEventWeek,
      setChartDataEventMonth,
      setChartDataViewsGeoCity,
      setChartDataViewsGeoProvince,
      setChartDataViewsGeoCountry,
      setChartDataClickSocial,
      setChartDataChannelMethod,
    );
  }, []);

  useEffect(() => {
    let card = index == 0 ? null : cards[index - 1].card_name;
    getAnalytic(
      token,
      card,
      setDateForFilter(dateRange[0].startDate),
      setDateForFilter(dateRange[0].endDate),
      filterDate,
      setSummary,
      setChartDataEventDay,
      setChartDataEventWeek,
      setChartDataEventMonth,
      setChartDataViewsGeoCity,
      setChartDataViewsGeoProvince,
      setChartDataViewsGeoCountry,
      setChartDataClickSocial,
      setChartDataChannelMethod,
    );
  }, [dateRange, filterDate, index]);

  return (
    <div className="flex flex-col flex-1 bg-white overflow-y-auto px-7 pt-5 pb-40 md:py-5 md:pb-40 gap-10 md:gap-5">
      <ToastContainer />
      <div className="flex flex-col md:flex-row gap-5">
        <div
          className="flex flex-row w-full md:w-1/4 gap-2 border border-gray-400 rounded-lg justify-between px-5 text-gray-900 cursor-pointer py-3 items-center"
          onClick={handleClickDateRange}
        >
          <div>
            {dateRange[0].startDate == null && dateRange[0].endDate == null
              ? "All time"
              : changeDateFilterFormat(dateRange[0].startDate) +
                " to " +
                changeDateFilterFormat(dateRange[0].endDate)}
          </div>
          <img
            src={calendar}
            alt="calendar icon"
            className="w-[20px] h-[20px]"
          />
        </div>

        <form className="flex flex-row w-full md:w-1/4 gap-2">
          <select
            id="cards"
            className="bg-white border border-gray-400 text-gray-900 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full px-5 py-3"
            value={index}
            onChange={(e) => setIndex(e.target.value)}
          >
            <option selected value={0}>
              All Cards
            </option>
            {cards.length > 0 &&
              cards.map((val, ind) => (
                <option key={ind + 1} value={ind + 1}>
                  {val.card_name}
                </option>
              ))}
          </select>
        </form>
      </div>
      <div className="flex flex-col md:flex-row gap-5">
        <div className="flex flex-col border border-gray-200 p-5 rounded-lg bg-white w-full md:w-1/4 gap-2">
          <div className="text-xl font-semibold">Total Users</div>
          <div className="text-xl">
            {summary.total_users != null
              ? summary.total_users.current
              : "No data"}
          </div>
          {summary.total_users != null && (
            <div className="flex flex-row gap-2">
              <div
                style={{
                  color:
                    summary.total_users.trend == "up"
                      ? "green"
                      : summary.total_users.trend == "flat"
                        ? "gray"
                        : "red",
                }}
              >
                {summary.total_users.percentage}%
              </div>
              <div>{summary.total_users.compare_message}</div>
            </div>
          )}
        </div>
        <div className="flex flex-col border border-gray-200 p-5 rounded-lg bg-white w-full md:w-1/4 gap-2">
          <div className="text-xl font-semibold">Total New Users</div>
          <div className="text-xl">
            {summary.total_new_users != null
              ? summary.total_new_users.current
              : "No data"}
          </div>
          {summary.total_new_users != null && (
            <div className="flex flex-row gap-2">
              <div
                style={{
                  color:
                    summary.total_new_users.trend == "up"
                      ? "green"
                      : summary.total_new_users.trend == "flat"
                        ? "gray"
                        : "red",
                }}
              >
                {summary.total_new_users.percentage}%
              </div>
              <div>{summary.total_new_users.compare_message}</div>
            </div>
          )}
        </div>
        <div className="flex flex-col border border-gray-200 p-5 rounded-lg bg-white w-full md:w-1/4 gap-2">
          <div className="text-xl font-semibold">Total Views</div>
          <div className="text-xl">
            {summary.total_views != null
              ? summary.total_views.current
              : "No data"}
          </div>
          {summary.total_views != null && (
            <div className="flex flex-row gap-2">
              <div
                style={{
                  color:
                    summary.total_views.trend == "up"
                      ? "green"
                      : summary.total_views.trend == "flat"
                        ? "gray"
                        : "red",
                }}
              >
                {summary.total_views.percentage}%
              </div>
              <div>{summary.total_views.compare_message}</div>
            </div>
          )}
        </div>
        <div className="flex flex-col border border-gray-200 p-5 rounded-lg bg-white w-full md:w-1/4 gap-2">
          <div className="text-xl font-semibold">Total Save Contact</div>
          <div className="text-xl">
            {summary.total_social_card_clicks != null
              ? summary.total_social_card_clicks.current
              : "No data"}
          </div>
          {summary.total_social_card_clicks != null && (
            <div className="flex flex-row gap-2">
              <div
                style={{
                  color:
                    summary.total_social_card_clicks.trend == "up"
                      ? "green"
                      : summary.total_social_card_clicks.trend == "flat"
                        ? "gray"
                        : "red",
                }}
              >
                {summary.total_social_card_clicks.percentage}%
              </div>
              <div>{summary.total_social_card_clicks.compare_message}</div>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-1 gap-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-row w-full justify-between">
            <div className="text-xl font-semibold">
              Total Event Count Over Time
            </div>
            <form className="flex flex-row w-full md:w-1/6 gap-2">
              <select
                id="timeDim"
                className="bg-white border border-gray-400 text-gray-900 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full px-5 py-3"
                value={eventCountFilter}
                onChange={(e) => setEventCountFilter(e.target.value)}
              >
                {filterEventCount.length > 0 &&
                  filterEventCount.map((val, ind) => (
                    <option key={ind + 1} value={val.value}>
                      {val.label}
                    </option>
                  ))}
              </select>
            </form>
          </div>

          <div className="flex flex-col h-[300px] bg-white justify-center items-center border border-gray-200 rounded-lg p-5">
            {chartDataEventDay == null ? (
              "No data"
            ) : (
              <Line
                options={chartDataEventOptions}
                data={
                  eventCountFilter == "daily"
                    ? chartDataEventDay
                    : eventCountFilter == "weekly"
                      ? chartDataEventWeek
                      : chartDataEventMonth
                }
              />
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-5">
          <div className="flex flex-col gap-3">
            <div className="flex flex-row w-full justify-between">
              <div className="text-xl font-semibold">
                Total Views by Geography
              </div>
              <form className="flex flex-row w-full md:w-1/6 gap-2">
                <select
                  id="timeDim"
                  className="bg-white border border-gray-400 text-gray-900 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full px-5 py-3"
                  value={viewsGeoFilter}
                  onChange={(e) => setViewsGeoFilter(e.target.value)}
                >
                  {filterViewsGeo.length > 0 &&
                    filterViewsGeo.map((val, ind) => (
                      <option key={ind + 1} value={val.value}>
                        {val.label}
                      </option>
                    ))}
                </select>
              </form>
            </div>
            <div className="flex flex-col h-[300px] bg-white justify-center items-center border border-gray-200 rounded-lg p-5">
              {chartDataViewsGeoCity == null ? (
                "No data"
              ) : (
                <Bar
                  options={chartDataViewsGeoOptions}
                  data={
                    viewsGeoFilter == "city"
                      ? chartDataViewsGeoCity
                      : viewsGeoFilter == "province"
                        ? chartDataViewsGeoProvince
                        : chartDataViewsGeoCountry
                  }
                />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="text-xl font-semibold">
              Total Clicks by Social Category
            </div>
            <div className="flex flex-col h-[300px] bg-white justify-center items-center border border-gray-200 rounded-lg p-5 mt-6">
              {chartDataClickSocial == null ? (
                "No data"
              ) : (
                <Bar
                  options={chartDataViewsGeoOptions}
                  data={chartDataClickSocial}
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="text-xl font-semibold">
            Total Users and Event by Channel and Method
          </div>
          <div className="w-full overflow-x-auto rounded-lg border-2 border-black">
            <table className="w-full min-w-[1000px] text-left border-collapse bg-white">
              <thead className="bg-primary text-white font-bold">
                <tr>
                  <th className="p-3 border-r border-white/20 cursor-pointer">
                    Channel
                  </th>
                  <th className="p-3 border-r border-white/20 cursor-pointer">
                    Method
                  </th>
                  <th className="p-3 border-r border-white/20 cursor-pointer">
                    Total New Users
                  </th>
                  <th className="p-3 border-r border-white/20 cursor-pointer">
                    Total Views
                  </th>
                  <th className="p-3 border-r border-white/20 cursor-pointer">
                    Total Contact Savings
                  </th>
                  <th className="p-3 cursor-pointer">
                    Total Social Card Click
                  </th>
                </tr>
              </thead>
              <tbody>
                {chartDataChannelMethod && chartDataChannelMethod.length > 0 ? (
                  chartDataChannelMethod.map((val, ind) => (
                    <tr
                      key={ind}
                      className={`border-b border-gray-200 ${ind % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-blue-50 transition-colors`}
                    >
                      <td className="p-3 whitespace-nowrap">{val.channel}</td>
                      <td className="p-3 whitespace-nowrap">{val.method}</td>
                      <td className="p-3 text-center">{val.total_new_users}</td>
                      <td className="p-3 text-center">{val.total_views}</td>
                      <td className="p-3 text-center">
                        {val.total_contact_saving}
                      </td>
                      <td className="p-3 text-center">
                        {val.total_social_card_clicks}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="p-10 text-center text-black text-gray-400"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <ModalClickDateRange
        openDateRange={openDateRange}
        setOpenDateRange={setOpenDateRange}
        dateRange={dateRange}
        setDateRange={setDateRange}
        setFilterDate={setFilterDate}
      />
    </div>
  );
};

export default AnalyticDashboard;
